import { nextTick } from "vue";

type ComponentRef = {
  $el?: HTMLElement;
};

type ComponentRefs = Record<string, ComponentRef | undefined>;

const multiselectResizeCleanup = new WeakMap<HTMLElement, () => void>();

export async function asyncfocusMultiselect(
  aliasName: string,
  multiSelectRefs: ComponentRefs,
): Promise<void> {
  await nextTick();

  const root = multiSelectRefs[aliasName]?.$el;
  if (!root?.querySelector) return;

  if (root.classList.contains("is-disabled")) return;

  const wrapper = root.querySelector<HTMLElement>(".multiselect-wrapper");
  if (!wrapper || wrapper.getAttribute("tabindex") === "-1") return;

  wrapper.focus();
}

export async function asyncfocusDatePicker(
  aliasName: string,
  pickerRefs: ComponentRefs,
): Promise<void> {
  await nextTick();

  const datepickerComponent = pickerRefs[aliasName]?.$el;
  if (!datepickerComponent) return;

  const visibleInput =
    datepickerComponent.nextElementSibling as HTMLElement | null;
  visibleInput?.focus();

  const inputField = datepickerComponent.querySelector<HTMLElement>(
    ".covomo-from-datepicker.input",
  );
  inputField?.focus();
}

export async function resizeMultiselectDropdown(
  refKey: string,
  multiSelectRefs: ComponentRefs,
): Promise<void> {
  await nextTick();

  requestAnimationFrame(() => {
    const multiselectElement = multiSelectRefs[refKey]?.$el;
    if (!multiselectElement) return;

    const bookingSection = multiselectElement.closest<HTMLElement>(
      ".booking-form-section",
    );

    if (!bookingSection) {
      multiselectElement.style.removeProperty("--ms-max-height");
      return;
    }

    multiselectResizeCleanup.get(multiselectElement)?.();

    const updateDropdown = () => {
      if (!multiselectElement.classList.contains("is-open")) {
        multiselectResizeCleanup.get(multiselectElement)?.();
        return;
      }

      const bookingNavigation = document.querySelector<HTMLElement>(
        '[aria-label="Tarif und Antragsnavigation"]',
      );

      const multiselectRect = multiselectElement.getBoundingClientRect();

      const navigationTop = Math.min(
        bookingNavigation?.getBoundingClientRect().top ?? window.innerHeight,
        window.innerHeight,
      );

      const gap = 8;
      const minimumComfortableHeight = 160;
      const maximumHeight = 240;

      const spaceBelow = Math.max(
        0,
        navigationTop - multiselectRect.bottom - gap,
      );
      const spaceAbove = Math.max(0, multiselectRect.top - gap);

      const opensUp =
        spaceBelow < minimumComfortableHeight && spaceAbove > spaceBelow;

      const availableSpace = opensUp ? spaceAbove : spaceBelow;
      const dropdownHeight = Math.max(
        80,
        Math.min(maximumHeight, availableSpace),
      );

      multiselectElement.style.setProperty(
        "--ms-max-height",
        `${dropdownHeight}px`,
      );

      bookingSection.classList.toggle(
        "booking-form-section--dropdown-opens-up",
        opensUp,
      );
    };

    const cleanup = () => {
      window.removeEventListener("resize", updateDropdown);
      window.removeEventListener("scroll", updateDropdown);

      multiselectElement.style.removeProperty("--ms-max-height");

      bookingSection.classList.remove(
        "booking-form-section--dropdown-opens-up",
      );

      multiselectResizeCleanup.delete(multiselectElement);
    };

    multiselectResizeCleanup.set(multiselectElement, cleanup);

    window.addEventListener("resize", updateDropdown);
    window.addEventListener("scroll", updateDropdown);

    updateDropdown();
  });
}

export function resetMultiselectDropdown(
  refKey: string,
  multiSelectRefs: ComponentRefs,
): void {
  const multiselectElement = multiSelectRefs[refKey]?.$el;
  if (!multiselectElement) return;

  const cleanup = multiselectResizeCleanup.get(multiselectElement);

  if (cleanup) {
    cleanup();
    return;
  }

  multiselectElement.style.removeProperty("--ms-max-height");
  multiselectElement
    .closest(".booking-form-section")
    ?.classList.remove("booking-form-section--dropdown-opens-up");
}
